import * as React from 'react'
import { useEffect, useMemo } from 'react'
import { Button, Tooltip } from '@fluentui/react-components'
import { AddFilled, AddRegular, ArrowDownloadFilled, ArrowDownloadRegular, bundleIcon } from '@fluentui/react-icons'
import { useDispatch } from 'react-redux'
import useRoles, { Role } from '../../../hooks/useRoles'
import { AppAction } from '../../../config/appConfig'
import { MenuItemKey } from '../../../components/MainMenu'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import { useFormikContext } from 'formik'
import useSubmitDocumentGenerationRequest from '../../../hooks/finance/report/useSubmitDocumentGenerationRequest'
import { DocumentGenerationType } from '../../../hooks/document/documentGenerationTypes'
import { parseFilterValuesToParams } from '../../BaseOverview/BaseOverview'
import { InvoiceStatus } from '../../../config/finance/invoiceConfig'
import { DateTime } from 'luxon'
import { FileAction } from '../../../config/fileConfig'
import BaseInvoiceOverview, { InvoiceFilterType } from './BaseInvoiceOverview'

const AddIcon = bundleIcon(AddFilled, AddRegular)
const DownloadIcon = bundleIcon(ArrowDownloadFilled, ArrowDownloadRegular)

export function HeaderActionsButtons() {
    const history = useHistory()
    const roles = useRoles()
    const { t: translate } = useTranslation()
    const { values } = useFormikContext<InvoiceFilterType>()

    const submitReportRequest = useSubmitDocumentGenerationRequest<InvoiceFilterType>(DocumentGenerationType.InvoiceXls)

    return (
        <>
            <Tooltip withArrow relationship={'label'} content={translate('action.create.invoice.title')}>
                <Button icon={<AddIcon />} appearance={'primary'} onClick={() => history.push('/invoice/add')} />
            </Tooltip>
            {roles.all([Role.Report]) && (
                <Tooltip withArrow relationship={'label'} content={translate('action.download.selection')}>
                    <Button icon={<DownloadIcon />} appearance={'secondary'} onClick={() => submitReportRequest(parseFilterValuesToParams(values))} />
                </Tooltip>
            )}
        </>
    )
}

export default function InvoiceOverview() {
    const dispatch = useDispatch()
    const location = useLocation()

    const pageParamsValues = useMemo<InvoiceFilterType>(() => {
        const params = new URLSearchParams(location.search)
        const crmRelations = params.get('crmRelations')?.split(',').filter(Boolean)
        const statuses = params.get('statuses')?.split(',').filter(Boolean) as InvoiceStatus[]
        if (!crmRelations || !statuses) {
            return undefined
        }

        return {
            crmRelations: crmRelations ?? [],
            statuses: statuses ?? [],
        }
    }, [])

    //Reset breadcrumbs and data
    useEffect(() => {
        dispatch({ type: AppAction.SetBreadcrumbs, payload: [MenuItemKey.Invoice] })
        dispatch({ type: FileAction.ResetCurrent })
    }, [])

    const defaultFilterValues = useMemo<InvoiceFilterType>(
        () => ({
            from: DateTime.local().startOf('year').minus({ year: 3 }).toISODate(),
            to: undefined,
            statuses: [InvoiceStatus.Draft, InvoiceStatus.Final, InvoiceStatus.Settled, InvoiceStatus.Credited],
            isSettled: null,
            hasTransaction: null,
            attributes: [],
            query: '',
            ...pageParamsValues,
        }),
        [],
    )

    return (
        <BaseInvoiceOverview
            defaultFilterValues={defaultFilterValues}
            headerActionsButtons={HeaderActionsButtons}
            localStorageKeyPrefix={pageParamsValues ? undefined : 'HammockInvoiceOverview'}
        />
    )
}
